import React, { useContext, useState, useEffect } from "react";
import { AiLabEnrolmentContext } from "../ai-lab/AiLab";
import {
  AI_LAB_COURSES,
  calculateEnrolmentDetails,
} from "../../constants/ai-lab-constants";
import { AlertTriangle } from "lucide-react";

// Modular sub-components
import EnrolHeaderCard from "./components/EnrolHeaderCard";
import ChildrenCounter from "./components/ChildrenCounter";
import ChildDetailsCard from "./components/ChildDetailsCard";
import ScheduleModal from "./components/ScheduleModal";
import CartCard from "./components/CartCard";

// Modular checkout hook
import useEnrolmentCheckout from "./hooks/useEnrolmentCheckout";

const EnrolForm = () => {
  const {
    numChildren,
    setNumChildren,
    childrenData,
    setChildrenData,
    otherQueries,
    setOtherQueries,
    parentsDetails,
  } = useContext(AiLabEnrolmentContext);

  const isStudent = parentsDetails?.accountType === "student";

  // Modal active state: null or { childIdx, courseId }
  const [activeModal, setActiveModal] = useState(null);

  // Use the extracted payment & checkout hook
  const { handleCheckout, loading, stage, error, setError } = useEnrolmentCheckout({ program: "ai-lab" });

  // Clear error on mount
  useEffect(() => {
    setError("");
  }, [setError]);

  const details = calculateEnrolmentDetails(childrenData);

  const handleCourseClick = (childIdx, courseId) => {
    setChildrenData((prev) => {
      const copy = [...prev];
      const child = { ...copy[childIdx] };
      const interests = child.interests || [];
      if (!interests.includes(courseId)) {
        child.interests = [...interests, courseId];
      }
      copy[childIdx] = child;
      return copy;
    });
    setActiveModal({ childIdx, courseId });
  };

  const handleSaveSchedule = (childIdx, courseId, day, timeSlot) => {
    setChildrenData((prev) => {
      const copy = [...prev];
      const child = { ...copy[childIdx] };
      const interests = child.interests || [];

      if (!interests.includes(courseId)) {
        child.interests = [...interests, courseId];
      }

      const schedule = { ...child.schedule };
      schedule[courseId] = { day, timeSlot };
      child.schedule = schedule;

      copy[childIdx] = child;
      return copy;
    });
  };

  const handleRemoveCourse = (childIdx, courseId) => {
    setChildrenData((prev) => {
      const copy = [...prev];
      const child = { ...copy[childIdx] };

      child.interests = (child.interests || []).filter((id) => id !== courseId);

      const schedule = { ...child.schedule };
      delete schedule[courseId];
      child.schedule = schedule;

      copy[childIdx] = child;
      return copy;
    });
  };

  const handleChildFieldChange = (childIdx, field, value) => {
    setChildrenData((prev) => {
      const copy = [...prev];
      copy[childIdx] = { ...copy[childIdx], [field]: value };
      return copy;
    });
  };

  // Find occupied slots for the child currently being edited in schedule modal
  const getOccupiedSlotsForActiveModal = () => {
    if (!activeModal) return [];
    const { childIdx, courseId } = activeModal;
    const child = childrenData[childIdx];
    const occupied = [];

    Object.entries(child.schedule || {}).forEach(([cId, sched]) => {
      if (cId !== courseId && sched.day && sched.timeSlot) {
        const cInfo = AI_LAB_COURSES.find((c) => c.id === cId);
        occupied.push({
          day: sched.day,
          timeSlot: sched.timeSlot,
          courseLabel: cInfo ? cInfo.label : "Other Course",
        });
      }
    });
    return occupied;
  };

  return (
    <div className="space-y-6 text-left max-w-7xl mx-auto">
      {/* Header UI Card */}
      <EnrolHeaderCard isStudent={isStudent} />

      {error && (
        <div className="p-3.5 bg-red-50 border border-red-200 text-red-700 rounded-2xl text-xs font-extrabold flex gap-2 items-center">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Grid Container */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* Left column: Form inputs */}
        <div className="lg:col-span-7 space-y-6">
          {/* Children counter */}
          {parentsDetails?.accountType !== "student" && (
            <ChildrenCounter
              numChildren={numChildren}
              onChangeChildren={setNumChildren}
            />
          )}

          {/* Children detail cards */}
          <div className="space-y-5">
            {childrenData.map((child, idx) => (
              <ChildDetailsCard
                key={idx}
                childIdx={idx}
                child={child}
                isChildUser={parentsDetails?.accountType === "student"}
                onFieldChange={(field, val) => handleChildFieldChange(idx, field, val)}
                onCourseClick={(courseId) => handleCourseClick(idx, courseId)}
              />
            ))}
          </div>

          {/* Queries / Remarks Textarea */}
          <div className="bg-white border border-gray-200 rounded-3xl p-5 sm:p-6 shadow-2xs space-y-2.5">
            <label className="block text-xs font-black text-gray-400 uppercase tracking-wider">
              Any specific queries or requests? (Optional)
            </label>
            <textarea
              placeholder="e.g. Schedule constraints, learning goals, details about prior coding experience, etc."
              value={otherQueries}
              onChange={(e) => setOtherQueries(e.target.value)}
              className="w-full text-xs sm:text-sm border border-gray-300 rounded-2xl px-4 py-3 focus:border-blue-500 focus:ring-1 focus:ring-blue-100 outline-none min-h-[96px] transition bg-white"
            />
          </div>
        </div>

        {/* Right column: Sticky checkout Summary */}
        <CartCard
          childrenData={childrenData}
          details={details}
          loading={loading}
          stage={stage}
          handleCheckout={handleCheckout}
          isStudent={isStudent}
        />
      </div>

      {/* Schedule picker Popup Modal */}
      {activeModal && (
        <ScheduleModal
          isOpen={!!activeModal}
          onClose={() => setActiveModal(null)}
          course={AI_LAB_COURSES.find((c) => c.id === activeModal.courseId)}
          currentSchedule={childrenData[activeModal.childIdx]?.schedule?.[activeModal.courseId]}
          occupiedSlots={getOccupiedSlotsForActiveModal()}
          onSave={(day, timeSlot) =>
            handleSaveSchedule(activeModal.childIdx, activeModal.courseId, day, timeSlot)
          }
          onRemove={() => handleRemoveCourse(activeModal.childIdx, activeModal.courseId)}
        />
      )}
    </div>
  );
};

export default EnrolForm;
